import axios, { AxiosInstance } from 'axios';
import { AuthResponse } from '../types/auth';
import { getValidatedApiUrl } from '../utils/envValidation';

// Get and validate API base URL from environment variables
// This ensures HTTPS is used in production and the URL is properly formatted
const API_BASE_URL = getValidatedApiUrl();

// Configure Axios instance with timeout and base URL
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authService = {
  /**
   * Authenticate admin user with email and password
   * @param email - Admin's email address (or identifier)
   * @param password - Admin's password
   * @returns Promise with authentication response containing tokens and user data
   */
  login: async (email: string, password: string): Promise<{ data: AuthResponse }> => {
    const response = await apiClient.post<{ success: boolean; message: string; data: AuthResponse }>('/auth/login', {
      identifier: email, // Backend expects 'identifier' field
      password,
    });
    
    // Backend wraps response in { success, message, data } structure
    const authData = response.data.data;
    
    // Validate that user is an admin
    if (authData.user.userType !== 'Admin') {
      throw new Error('Access denied. Admin privileges required.');
    }
    
    // Store tokens and user data in sessionStorage
    if (authData.accessToken) {
      sessionStorage.setItem('authToken', authData.accessToken);
    }
    if (authData.refreshToken) {
      sessionStorage.setItem('refreshToken', authData.refreshToken);
    }
    sessionStorage.setItem('user', JSON.stringify(authData.user));
    
    return { data: authData };
  },

  /**
   * Log out the current user by removing the authentication tokens
   */
  logout: async (): Promise<void> => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
  },

  /**
   * Retrieve the stored authentication token
   * @returns The authentication token or null if not found
   */
  getToken: (): string | null => {
    return sessionStorage.getItem('authToken');
  },

  /**
   * Retrieve the stored user data
   * @returns The user object or null if not found
   */
  getUser: (): AuthResponse['user'] | null => {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Check if user is authenticated and is an admin
   * @returns True if user is authenticated and has admin role
   */
  isAdmin: (): boolean => {
    const user = authService.getUser();
    return user?.userType === 'Admin';
  },

  /**
   * Request password reset by sending OTP to email
   * @param email - User's email address
   */
  forgotPassword: async (email: string): Promise<{ data: { success: boolean; message: string; data: { attemptsRemaining?: number } } }> => {
    const response = await apiClient.post<{ success: boolean; message: string; data: { attemptsRemaining?: number } }>('/auth/forgot-password', {
      identifier: email,
    });
    return response;
  },

  /**
   * Verify the reset code (OTP)
   * @param email - User's email address
   * @param otp - Verification code
   */
  verifyResetCode: async (email: string, otp: string): Promise<void> => {
    await apiClient.post('/auth/verify-reset-code', {
      identifier: email,
      otp,
    });
  },

  /**
   * Reset password with OTP
   * @param email - User's email address
   * @param otp - Verification code
   * @param newPassword - New password
   */
  resetPassword: async (email: string, otp: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', {
      identifier: email,
      otp,
      newPassword,
    });
  },
};
